import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import budget2024Logo from "@assets/logo-budget2024-v3.svg";
import Accordion from "@components/Accordion/Accordion";
import { sideDrawerNavigation } from "@components/Header/constant";
import { useCategoriesFromPath } from "@components/Header/helpers";
import Logo from "@components/Header/Logo/Logo";
import SearchTrigger from "@components/Header/SearchTrigger/SearchTrigger";
import Icon from "@components/Icon/Icon";
import useOKTAUserStore, { OKTAUserType } from "@store/useOKTAUserStore";
import { sectionNavigationItems } from "@util/constant";
import {
  mySPHOpenLogin,
  mySPHOpenLogout,
  mySPHOpenSignUp,
} from "@util/helpers";
import cx from "classnames";

interface SideMenuProps {
  isSideMenuOpen: boolean;
  setIsSideMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function SideMenu({
  isSideMenuOpen,
  setIsSideMenuOpen,
}: SideMenuProps): React.ReactElement {
  const OKTAUserInfo = useOKTAUserStore((state) => state.userInfo);

  const handleClose = () => {
    setIsSideMenuOpen(false);
  };

  return (
    <SideMenuWrapper isOpen={isSideMenuOpen}>
      <>
        <SideMenuHeader handleClose={handleClose} />
        <SideMenuNavItems />
        <RegisterAccountBlock OKTAUserInfo={OKTAUserInfo} />
      </>
    </SideMenuWrapper>
  );
}

interface SideMenuWrapperProps {
  children: React.ReactElement;
  isOpen: boolean;
}

function SideMenuWrapper({
  children = <></>,
  isOpen = false,
}: SideMenuWrapperProps): React.ReactElement | null {
  return (
    <>
      <div
        data-testid="SideMenuWrapper"
        className={cx(
          "fixed -left-full bottom-0 top-0 z-[200] w-full overflow-y-scroll duration-300 lg:-left-[400px] lg:w-[400px]",
          isOpen && "translate-x-full lg:translate-x-[400px]"
        )}
      >
        {children}
      </div>
      <div
        className={cx(
          "left-0 right-0 top-0 z-[100] h-full w-full opacity-0 duration-300",
          isOpen && "fixed lg:bg-black lg:opacity-50"
        )}
      ></div>
    </>
  );
}

interface SideMenuHeaderProps {
  handleClose: () => void;
}

function SideMenuHeader({ handleClose }: SideMenuHeaderProps) {
  return (
    <div className="flex items-center bg-gray-850 py-2 pl-5 pr-2">
      <div className="w-3/5 sm:w-3/4">
        <Logo style={{ width: "300px", height: "30px" }} />
      </div>
      <div className="ml-auto flex items-center justify-end">
        <div className="p-2 lg:hidden">
          <SearchTrigger />
        </div>
        <div
          data-testid="SideMenuHeaderCloseIcon"
          className={cx(
            "cursor-pointer p-2 opacity-100 duration-300",
            "hover:opacity-75"
          )}
          onClick={handleClose}
        >
          <Icon type="close" />
        </div>
      </div>
    </div>
  );
}

function SideMenuNavItems() {
  const { parentCategory, childCategory } = useCategoriesFromPath();
  const [expandedAccordionId, setExpandedAccordionId] = useState(
    sectionNavigationItems?.[parentCategory]?.key
  );

  useEffect(() => {
    setExpandedAccordionId(sectionNavigationItems?.[parentCategory]?.key);
  }, [parentCategory]);

  return (
    <nav className="divide flex flex-col divide-y bg-gray-150">
      {sideDrawerNavigation.map((sideNavigationItem) => {
        if (sideNavigationItem === undefined) return;

        const { key, link, label, children } = sideNavigationItem;
        const isCurrentItemExpanded = expandedAccordionId === key;
        const isCurrentItemMatchUrl =
          key === sectionNavigationItems?.[parentCategory]?.key;
        const isCurrentItemChildMatchUrl = children?.includes(
          `${parentCategory}/${childCategory}`
        );

        return (
          <Accordion
            key={key}
            id={key}
            label={label}
            link={
              key === "singapore-budget-2024"
                ? `${link}?ref=hamburger-menu`
                : link
            }
            onToggle={(_, accordionId) => {
              setExpandedAccordionId(isCurrentItemExpanded ? "" : accordionId);
            }}
            isExpanded={isCurrentItemExpanded}
            isMatchUrl={isCurrentItemMatchUrl}
            isChildMatchUrl={isCurrentItemChildMatchUrl}
            icon={
              key === "singapore-budget-2024" ? (
                <img
                  src={budget2024Logo}
                  width={32}
                  height={45}
                  alt="Singapore Budget 2024"
                  className="mr-2 inline"
                />
              ) : undefined
            }
          >
            {children
              ? children.map((subNavigationId: string) => {
                  const subSideNavigationItem =
                    sectionNavigationItems?.[subNavigationId];

                  return subSideNavigationItem ? (
                    <div
                      className={cx("truncate px-3 leading-[38px]", {
                        "bg-gray-650 text-white":
                          subSideNavigationItem.key ===
                          `${parentCategory}/${childCategory}`,
                      })}
                      key={subSideNavigationItem.link}
                    >
                      <Link
                        className="font-poppins text-base"
                        to={subSideNavigationItem.link}
                        reloadDocument
                      >
                        {subSideNavigationItem.label}
                      </Link>
                    </div>
                  ) : null;
                })
              : null}
          </Accordion>
        );
      })}
    </nav>
  );
}

interface RegisterAccountBlockProps {
  OKTAUserInfo: OKTAUserType;
}

export function RegisterAccountBlock({
  OKTAUserInfo,
}: RegisterAccountBlockProps) {
  const displayName = OKTAUserInfo?.display_name;
  const email = OKTAUserInfo?.loginid;

  const AccountInfoDisplay = () => {
    return (
      <div data-testid="AccountInfoDisplay">
        <div className="mb-3 font-public-sans text-base font-bold text-gray-900">
          Welcome, {displayName}
        </div>
        <div className="mb-3 border-b border-gray-450 pb-3 font-public-sans text-base font-medium text-gray-900">
          {email}
        </div>
        <div
          className={cx(
            "font-poppins font-semibold text-gray-850",
            "hover:cursor-pointer"
          )}
          onClick={mySPHOpenLogout}
        >
          LOG OUT
        </div>
      </div>
    );
  };

  const RegisterAccountDisplay = () => {
    return (
      <div data-testid="RegisterAccountDisplay">
        <div className="mb-6 font-poppins text-base text-gray-650">
          Create a free account with Business Times for seamless access across
          SPH Media products.
        </div>
        <div className="flex items-center font-poppins font-semibold text-black">
          <div
            className={cx(
              "rounded-full border border-gray-525 bg-gray-100 px-6 py-1.5 text-base duration-300",
              "hover:cursor-pointer hover:bg-gray-300"
            )}
            onClick={mySPHOpenSignUp}
          >
            REGISTER
          </div>
          <div
            className={cx("ml-1 border px-6 py-1.5", "hover:cursor-pointer")}
            onClick={mySPHOpenLogin}
          >
            LOGIN
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-250 px-6 py-4">
      {displayName ? <AccountInfoDisplay /> : <RegisterAccountDisplay />}
    </div>
  );
}
